import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

import './section.scss';
import './hero-container.scss';
import './marketing-section.scss';

import SliderNavigationButtons from './SliderNavigationButtons';
import PackagesSlider from './PackagesSlider';
import Settings from '../../../services/settings.service';
import RequirePermission from '../RequirePermission';
import { Helmet } from 'react-helmet';

const TEMPORARY_URL_PREFIX = `${Settings.REACT_APP_API_BASE_URL}images`;
const backgroundGradient = (background_color1, background_color2) => {
  let isColor1Valid = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(
    background_color1
  );
  let isColor2Valid = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(
    background_color2
  );
  if (isColor2Valid && isColor1Valid)
    return `linear-gradient(298.62deg, ${background_color1} 0%, ${background_color2} 121.22%)`;
  return isColor1Valid ? background_color1 : background_color2;
};
const ButtonsGroup = ({ buttons, isImageButton, isDownloadApp, color }) => {
  const isDarkModeSecondaryButton =
    color && !['#000', '#000000', 'black'].includes(color?.toLowerCase());

  return (
    <div className="buttons-group">
      {buttons.map(({ url, text, icon }, index) => {
        // Define permission keys for each button based on the text
        let permissionKey;
        if (text === 'Browse All Bundles') permissionKey = 'bundles';
        else if (text === 'Find Your Bundle')
          permissionKey = 'bundles_find_your_bundle';

        return (
          <>
            <RequirePermission permission={permissionKey} key={text + url}>
              {isImageButton ? (
                isDownloadApp ? (
                  <a href={url} target="_blank">
                    <img
                      alt={text}
                      loading="lazy"
                      className="download-img"
                      src={TEMPORARY_URL_PREFIX + icon}
                    />
                  </a>
                ) : (
                  <Link to={url}>
                    <img
                      alt={text}
                      loading="lazy"
                      className="download-img"
                      src={TEMPORARY_URL_PREFIX + icon}
                    />
                  </Link>
                )
              ) : (
                <Link
                  className={`lebara-button ${index === 0 ? 'primary' : ''} ${
                    isDarkModeSecondaryButton ? 'dark' : ''
                  }`}
                  to={url}
                >
                  {text}
                </Link>
              )}
            </RequirePermission>
          </>
        );
      })}
    </div>
  );
};

const SectionContainer = ({
  children,
  isFullHeight,
  lang,
  isHero,
  backgroundImage,
  isBackgroundWithText,
  isSlider,
  containerRef,
  reverse_direction,
}) => {
  const content = (
    <div
      ref={containerRef}
      className={`${isHero ? 'hero-container' : 'marketing-section'} ${
        isFullHeight ? 'full-height' : ''
      } ${isBackgroundWithText ? 'background-text-wrapper' : ''}`}
      style={
        backgroundImage
          ? {
              backgroundImage: `url(${TEMPORARY_URL_PREFIX}${backgroundImage})`,
            }
          : {}
      }
    >
      <div
        className={`inner-container ${reverse_direction ? 'reverse ' : ''}${
          lang === 'ar' && 'rtl'
        }`}
      >
        {children}
      </div>
    </div>
  );

  return isSlider ? (
    <div
      className="dark-background"
      style={{ maxWidth: '1920px', margin: '0 auto' }}
    >
      {content}
    </div>
  ) : (
    content
  );
};

const Hero = ({
  title,
  subtitle,
  desktop_image,
  buttons = [],
  lang,
  color,
  background_color1,
  background_color2,
}) => {
  let background;
  if (!background_color1 && !background_color2)
    background = 'linear-gradient(307.04deg, #00aaeb 16.9%, #3824d6 90.84%)';
  else background = backgroundGradient(background_color1, background_color2);
  return (
    <div
      className={`hero-container ${lang === 'ar' ? 'rtl' : ''}`}
      style={{ background }}
    >
      <div className="text-container">
        <h1 style={{ color }} className="hero-title">
          {title}
        </h1>
        <p style={{ color }} className="hero-subtitle">
          {subtitle}
        </p>
        <ButtonsGroup color={color} buttons={buttons} />
      </div>
      <div>
        <img src={`${TEMPORARY_URL_PREFIX}${desktop_image}`} alt={title}></img>
      </div>
    </div>
  );
};

const BackgroundSection = ({
  title,
  subtitle,
  desktop_image,
  buttons = [],
  lang,
  color,
  reverse_direction,
}) => {
  return (
    <SectionContainer
      backgroundImage={desktop_image}
      isBackgroundWithText={true}
      reverse_direction={reverse_direction}
    >
      <div
        className={`text-container ${
          !reverse_direction ? 'wide' : ''
        } text-container-market`}
      >
        <h2 className="title-background-sec" style={{ color }}>
          {title}
        </h2>
        <p className="subtitle-background-sec" style={{ color }}>
          {subtitle}
        </p>
        <ButtonsGroup buttons={buttons} color={color || 'white'} />
      </div>
    </SectionContainer>
  );
};

const SlidesSections = ({ lang, slides }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const previous = () => selectedIndex && setSelectedIndex((index) => --index);
  const next = () => {
    selectedIndex < slides.length - 1 && setSelectedIndex((index) => ++index);
  };
  //Lazy load image
  const [isLoaded, setIsLoaded] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref?.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsLoaded(true);
        observer.disconnect();
      }
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [ref]);

  const currentSlide = slides?.[selectedIndex];
  return (
    <SectionContainer
      lang={lang}
      isFullHeight={true}
      containerRef={ref}
      backgroundImage={isLoaded && currentSlide?.desktop_image}
      isSlider={true}
    >
      <div className="slider-text-container">
        {slides.length > 1 ? (
          <SliderNavigationButtons
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            next={next}
            previous={previous}
            count={slides.length}
            isFullHeight={true}
          />
        ) : (
          ''
        )}
        <h2 className="hero-title">{currentSlide?.title}</h2>
        <p className="hero-title regular-font">{currentSlide?.subtitle}</p>
      </div>
    </SectionContainer>
  );
};

const ImageSection = ({
  title,
  subtitle,
  desktop_image,
  buttons = [],
  lang,
  reverse_direction,
  isDownloadApp = false,
  isPackagesSlider = false,
  background_color1,
  background_color2,
  color,
}) => {
  const background = backgroundGradient(background_color1, background_color2);
  return (
    <div
      className={`marketing-section ${lang === 'ar' ? 'rtl' : ''}`}
      style={{ background }}
    >
      <div
        className={
          'inner-container ' +
          `${reverse_direction ? 'reverse ' : ''}` +
          `${isPackagesSlider ? 'package-slider ' : 'with-image '}` +
          `${isDownloadApp ? 'download-app ' : ''}` +
          `${'small-container'}`
        }
      >
        <div className="text-container text-container-market">
          <h2 className="title large dark-text" style={{ color }}>
            {title}
          </h2>
          <p className="subtitle large dark-text" style={{ color }}>
            {subtitle}
          </p>

          <ButtonsGroup
            buttons={buttons}
            isImageButton={isDownloadApp}
            isDownloadApp={isDownloadApp}
            color={color}
          />
        </div>
        {isPackagesSlider ? (
          <PackagesSlider />
        ) : (
          <img
            loading="lazy"
            className="main-image"
            src={desktop_image && `${TEMPORARY_URL_PREFIX}${desktop_image} `}
            alt={title}
          ></img>
        )}
      </div>
    </div>
  );
};

const PostPaidSection = ({
  title,
  subtitle,
  desktop_image,
  buttons = [],
  lang,
  reverse_direction,
  isDownloadApp = false,
}) => {
  return (
    <div className={`marketing-section ${lang === 'ar' ? 'rtl' : ''}`}>
      <div
        className={
          'inner-container package-slider ' +
          (reverse_direction ? 'package-slider-reverse ' : '')
        }
      >
        <div className="postpaid-text-container ">
          <h2 className="title large dark-text">{title}</h2>
          <p className="subtitle large dark-text">{subtitle}</p>
          <ButtonsGroup buttons={buttons} />
        </div>
        <PackagesSlider />
      </div>
    </div>
  );
};

export const LinkSectionButton = ({
  isMainLink,
  imgSrc,
  text,
  url,
  state,
  linkParams,
  toggleNav = () => {},
}) => {
  const searchParams = new URLSearchParams();
  searchParams.append('category', linkParams);
  return (
    <Link
      className={`button-link ${isMainLink ? 'main-link' : ''} `}
      to={{
        pathname: url,
        state: state,
        search: linkParams ? `?${searchParams.toString()}` : '',
      }}
      onClick={toggleNav}
    >
      <img loading="lazy" src={imgSrc} alt={text} />
      {text}
    </Link>
  );
};

const LinksSection = ({ title, buttons = [], lang }) => {
  return (
    <div
      className={`marketing-section button-links-container ${
        lang === 'ar' ? 'rtl' : ''
      }`}
    >
      <div className="inner-container block">
        <h2 className="title large dark-text">{title}</h2>
        <div>
          <div className="first-links-row">
            {buttons?.slice(0, 3)?.map(({ url, text, icon, state }, index) => (
              <LinkSectionButton
                key={text + url}
                isMainLink={true}
                imgSrc={TEMPORARY_URL_PREFIX + icon}
                text={text}
                url={url}
                state={state}
              />
            ))}
          </div>
          <div className="second-links-row">
            {buttons?.slice(3)?.map(({ url, text, icon, state }, index) => (
              <LinkSectionButton
                key={text + url}
                imgSrc={TEMPORARY_URL_PREFIX + icon}
                text={text}
                url={url}
                state={state}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ sectionType, ...section }) => {
  switch (sectionType) {
    case 'hero':
      return <Hero {...section} />;
    case 'marketing_with_background':
      return <BackgroundSection {...section} />;
    case 'marketing_with_image':
      return <ImageSection {...section} />;
    case 'download_app':
      return <ImageSection {...section} isDownloadApp={true} />;
    case 'packages_slider':
      return <PostPaidSection {...section} />;
    case 'marketing_slider':
      return <SlidesSections {...section} isDownloadApp={true} />;
    case 'links_section':
      return <LinksSection {...section} isDownloadApp={true} />;
    default:
      return null;
  }
};

const SectionWrapper = (sectionData) => {
  const section = <Section {...sectionData} />;
  return section ? <div>{section}</div> : null;
};
export default SectionWrapper;
